/**
 * ADR-0185 §3.4 — per-machine lanes: hand-composed DOM timelines (house CSS,
 * no charting dependency — interactivity needs real elements). Jobs render as
 * labeled segments, changeover gaps color by measured class, unstaffed hours
 * shade from the calendar (the capacity face), pins wear a padlock, at-risk
 * jobs a red edge, fallback-priced changeovers a dashed edge. Hover carries
 * the job's story: promise + routing milestone, run hours (+ basis when
 * bound), setup-key values (+ basis when bound). Changeover gaps carry the
 * same hover-card treatment (never a native title).
 *
 * Phase A (Addendum 7) — expansion redesign, owner-ruled via the mockup: no
 * gesture ever inflates a row or its shapes. Expanding a machine ADDS
 * same-height labeled tracks beneath. Labels live ON rows (owner's Phase-A
 * review, 2026-09-08): the working row's label cell carries the machine name
 * AND its bare track label when expanded; each added track's cell carries
 * its own. There is no caption row — an empty liner read as a missing
 * track. NO helper prose. Expand one, several, or all (header buttons); the
 * choice is remembered per browser (the view owns that state).
 *
 * Phase B (the Refine loop) — the working row is the REFINED plan (label
 * "Refined ↑"); expanding adds "Optimized" (the standing plan) and "Plex"
 * beneath. Clicking a job on the Refined row opens the move popover (the
 * view owns it — this component reports the click with its anchor). Moves,
 * never removals: running jobs don't take a click.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { formatTimestamp } from "../../../../lib/formatTime.js";
import type { BoardCalendarWindow, BoardLane, BoardLaneJob } from "../../boardTypes.js";
import { ChangeoverHoverCard } from "./ChangeoverHoverCard.js";
import {
  jobLabel,
  jobSegmentClasses,
  drawsInWindow,
  gapBandSpan,
  gapHoverRows,
  gapIndicator,
  hatchExempt,
  plexOverdue,
  unstaffedSpans,
  laneExtent,
  movableOnTrack,
  pieceLine,
  maxSetupHours,
  movedMarkerVisible,
  positionIn,
  rankChangeLine,
  setupColor,
  stackRows,
  staffingBands,
  timeTicks,
  zoomWindow,
  type TrackKind,
  type ZoomLevel,
  hoverCardPosition,
} from "./laneMath.js";

function IconLock({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-label="Pinned" role="img">
      <path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zm-3 8V7a3 3 0 1 1 6 0v3H9z" />
    </svg>
  );
}

const ZOOMS: Array<{ key: ZoomLevel; label: string }> = [
  { key: "today", label: "Today" },
  { key: "three_days", label: "3 days" },
  { key: "full", label: "Full window" },
];

type HoverState =
  | { kind: "job"; job: BoardLaneJob; machine: string; track: TrackKind; x: number; y: number; cw: number; ch: number }
  | { kind: "changeover"; job: BoardLaneJob; x: number; y: number; cw: number; ch: number }
  | { kind: "gap"; job: BoardLaneJob; x: number; y: number; cw: number; ch: number };

/**
 * One labelled line in a hover card: a bold label and its value, aligned down
 * the card with every other line (the card is the grid; a row is `contents`).
 * Owner, 2026-09-19: "I do like the details, but when I hover I need easily
 * identified info. Bold headers, less verbose language."
 */
function Row(props: { label: string; children: ReactNode }) {
  return (
    <div className="boardHoverRow">
      <strong>{props.label}</strong>
      <span>{props.children}</span>
    </div>
  );
}

/** A full-width line — the header, the muted footer notes, and a basis line. */
function Note(props: { muted?: boolean; className?: string; children: ReactNode }) {
  return (
    <div className={`boardHoverWide${props.muted ? " muted" : ""}${props.className ? ` ${props.className}` : ""}`}>
      {props.children}
    </div>
  );
}

/**
 * Iteration 4 — how a figure was arrived at, on its own full-width line under
 * the row it explains rather than inside the value column. The basis strings
 * are the tenant's own prose and have grown long ("first-time-job median on
 * this machine, 110 jobs, last 5 years"); in the value column they wrapped
 * the row to three lines on every bar. The card already had a full-width
 * idiom for its header and footer, so the basis borrows that one.
 */
function Basis(props: { children: ReactNode }) {
  return <Note muted className="boardHoverBasis">{props.children}</Note>;
}
export function Lanes(props: {
  /** The working row per machine (Phase B: the Refined plan). */
  lanes: BoardLane[];
  /** The bare label the working row wears when expanded ("Refined ↑" in refine context, "Optimized" otherwise). */
  workingLabel: string;
  /** Phase B — the standing (Optimized) plan's track per machine, added on expand; omit to add none. */
  optimizedLanes?: BoardLane[];
  /** Phase A — the Plex schedule's own track per machine; [] when the solve ran without the binding. */
  scheduledLanes: BoardLane[];
  /** Phase B — a click on a job on ANY track (never a running one) opens the move popover against the Refined row; absent = not editable. */
  onJobClick?: (job: BoardLaneJob, machine: string, anchor: HTMLElement) => void;
  /** Phase B — jobs with a pending move wear the pending mark. */
  pendingJobs?: Set<string>;
  /** class label → measured changeover count for the hover cards' sample line. */
  measuredCounts?: Map<string, number | null>;
  calendarWindows: BoardCalendarWindow[];
  expandedMachines: Set<string>;
  onToggleMachine: (machine: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** what-if frame: jobs to outline as changed vs the standing plan. */
  highlightJobs?: Set<string>;
  /** Phase B iteration 1 — the Find box, rendered into the board header. */
  findSlot?: ReactNode;
  /**
   * Phase B iteration 1 — the job Find is sitting on. Highlighted on EVERY
   * track (Refined, Optimized and Plex), because the point of finding a job
   * is to compare where the three plans put it.
   */
  findActiveJob?: string | null;
}) {
  const [zoom, setZoom] = useState<ZoomLevel>("today");
  const [hover, setHover] = useState<HoverState | null>(null);

  const extent = useMemo(() => laneExtent(props.lanes, Date.now()), [props.lanes]);
  const window = useMemo(
    () => (extent ? zoomWindow(zoom, extent, Date.now()) : null),
    [extent, zoom]
  );
  const bands = useMemo(
    () => (window ? staffingBands(props.calendarWindows, window) : []),
    [props.calendarWindows, window]
  );
  const ticks = useMemo(() => (window ? timeTicks(window, zoom) : []), [window, zoom]);
  const max = useMemo(() => maxSetupHours(props.lanes), [props.lanes]);

  // Finding a job that then draws nowhere is a dead end: the zoom windows to
  // the working present, and plenty of the book sits outside it. When Find
  // lands on a job whose bar is off the current window, widen to Full so the
  // highlight is actually on screen. Widening is the only automatic move —
  // the zoom is never narrowed for the user, and clearing Find leaves the
  // zoom where he can see it.
  const findActiveJob = props.findActiveJob ?? null;
  useEffect(() => {
    if (!findActiveJob || !window) return;
    const inWindow = [props.lanes, props.optimizedLanes ?? [], props.scheduledLanes].some((set) =>
      set.some((lane) =>
        lane.jobs.some((j) => j.job === findActiveJob && drawsInWindow(j, window))
      )
    );
    if (!inWindow) setZoom("full");
    // The window object is derived from zoom; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findActiveJob]);

  if (!extent || !window) {
    return <div className="muted">This plan has no timed jobs to draw.</div>;
  }

  const nowFrac = positionIn(window, Date.now());
  const showNow = Date.now() >= window.startMs && Date.now() <= window.endMs;
  const scheduledByMachine = new Map(props.scheduledLanes.map((l) => [l.machine, l]));
  const optimizedByMachine = new Map((props.optimizedLanes ?? []).map((l) => [l.machine, l]));

  /**
   * One timeline track — the working row, the expanded machine's "Optimized"
   * track (the standing plan, muted), or its "Plex" track — Plex's order laid
   * out from now: same encodings, muted via the track classes; all share the
   * one axis, bands and gridlines.
   */
  const renderTrack = (machine: string, jobs: BoardLaneJob[], track: TrackKind) => {
    const plex = track === "plex";
    // Item E: every track is editable — a job is moved from wherever it is seen.
    const editable = !!props.onJobClick;
    // Only the Plex line may stack. Since 2026-10-08 it is Plex's order laid
    // out from now, so it is sequential and this draws one row; the stacker
    // stays wired for the one case Plex still reports concurrency — two jobs
    // it calls started on one machine (plex_started_count).
    const stack = plex ? stackRows(jobs, window) : { row: jobs.map(() => 0), rows: 1 };
    const ROW_H = 22;
    return (
    <div
      className={`boardLaneTrack${plex ? " boardLaneTrackPlex" : ""}${track === "optimized" ? " boardLaneTrackOptimized" : ""}${editable ? " boardLaneTrackEditable" : ""}`}
      style={stack.rows > 1 ? { height: `${stack.rows * ROW_H}px` } : undefined}
    >
      {bands.map((b, i) => (
        <div
          key={i}
          className={b.kind === "unstaffed" ? "boardShadeUnstaffed" : "boardShadeReduced"}
          style={{ left: `${b.from * 100}%`, width: `${(b.to - b.from) * 100}%` }}
          title={b.kind === "unstaffed" ? "Unstaffed hours" : "Reduced staffing"}
        />
      ))}
      {ticks.map((t) => (
        <div
          key={t.ms}
          className={`boardTickLine${t.kind === "day" ? " boardTickLineDay" : ""}`}
          style={{ left: `${t.frac * 100}%` }}
        />
      ))}
      {showNow ? <div className="boardNowLine" style={{ left: `${nowFrac * 100}%` }} title="Now" /> : null}
      {jobs.map((j, i) => {
        if (!drawsInWindow(j, window)) return null;
        const startMs = Date.parse(j.start!);
        const endMs = Date.parse(j.end!);
        const from = positionIn(window, startMs);
        const to = positionIn(window, endMs);
        const width = Math.max(0.004, to - from);
        // The changeover gap into this job: from the previous job's
        // end to this start, colored by this job's setup class.
        const prev = jobs[i - 1];
        const prevEnd = prev?.end ? Date.parse(prev.end) : NaN;
        const gapFrom = !Number.isNaN(prevEnd) ? positionIn(window, prevEnd) : null;
        const subRow = stack.row[i] ?? 0;
        // Sub-row placement. The JOB bar fills its sub-row; the changeover
        // band and the no-gap mark are THIN by design (styles.css insets them
        // to the middle of the row), so they need their own sub-row geometry.
        // Spreading the job's style onto them set an explicit height with
        // `bottom: auto`, which overrode the inset and inflated the band to
        // full height — the "fat lines" the owner saw on H28 and H38, the two
        // machines where Plex had booked jobs over each other (2026-09-19).
        // Sub-rows only ever occur on the Plex line, which is why it showed up
        // there and nowhere else.
        const subRowTop = subRow * ROW_H + 2;
        const subRowH = ROW_H - 4;
        const rowStyle = stack.rows > 1
          ? { top: `${subRowTop}px`, height: `${subRowH}px`, bottom: "auto" as const }
          : {};
        // Same proportions the stylesheet gives an unstacked row: the band
        // sits in the middle quarter, the mark spans all but the outer 15%.
        const bandRowStyle = stack.rows > 1
          ? { top: `${subRowTop + Math.round(subRowH * 0.38)}px`,
              height: `${Math.max(3, Math.round(subRowH * 0.24))}px`,
              bottom: "auto" as const }
          : {};
        const markRowStyle = stack.rows > 1
          ? { top: `${subRowTop + Math.round(subRowH * 0.15)}px`,
              height: `${Math.max(4, Math.round(subRowH * 0.7))}px`,
              bottom: "auto" as const }
          : {};
        const highlighted = track === "working" && (props.highlightJobs?.has(j.job) ?? false);
        const found = props.findActiveJob != null && props.findActiveJob === j.job;
        const clickable = movableOnTrack(track, j, editable);
        const pending = track === "working" && (props.pendingJobs?.has(j.job) ?? false);
        // Item C — the setup-sufficiency indicator. Dormant since 2026-10-08:
        // the Plex line is a laid-out plan, the hub serves no grade, and the
        // gap wears the changeover band below as the Optimized line's does.
        const gap = plex ? gapIndicator(j) : null;
        // The band spans the served gap; when Plex left none, it spans the
        // setup the job NEEDS, ending at the job's start (its class hours).
        const span = gap ? gapBandSpan(j, startMs) : null;
        const bandFrom = span ? positionIn(window, span.fromMs) : null;
        const gapHasWidth = bandFrom != null && from > bandFrom;
        return (
          <div key={j.job} style={{ display: "contents" }}>
            {gap ? (
              <div
                className={`${gapHasWidth ? "boardGapBand" : "boardGapMark"} ${gap.className}`}
                style={gapHasWidth
                  ? { left: `${bandFrom! * 100}%`, width: `${(from - bandFrom!) * 100}%`, ...bandRowStyle }
                  : { left: `${from * 100}%`, ...markRowStyle }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.closest(".boardLanes")!.getBoundingClientRect();
                  setHover({ kind: "gap", job: j, x: e.clientX - rect.left, y: e.clientY - rect.top, cw: rect.width, ch: rect.height });
                }}
                onMouseLeave={() => setHover(null)}
              />
            ) : null}
            {!gap && gapFrom != null && j.setup_class && from > gapFrom ? (
              <div
                className={`boardSetupGap${j.setup_cost_fallback ? " boardSetupGapEstimated" : ""}`}
                style={{
                  left: `${gapFrom * 100}%`,
                  width: `${(from - gapFrom) * 100}%`,
                  background: setupColor(j.setup_hours, max),
                  ...bandRowStyle,
                }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.closest(".boardLanes")!.getBoundingClientRect();
                  setHover({ kind: "changeover", job: j, x: e.clientX - rect.left, y: e.clientY - rect.top, cw: rect.width, ch: rect.height });
                }}
                onMouseLeave={() => setHover(null)}
              />
            ) : null}
            <button
              type="button"
              className={jobSegmentClasses({
                atRisk: j.at_risk,
                running: j.running,
                changed: highlighted,
                clickable,
                pending,
                placed: !!j.placement,
                found,
                track,
              })}
              style={{ left: `${from * 100}%`, width: `${width * 100}%`, ...rowStyle }}
              onClick={clickable ? (e) => { setHover(null); props.onJobClick!(j, machine, e.currentTarget); } : undefined}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.closest(".boardLanes")!.getBoundingClientRect();
                setHover({ kind: "job", job: j, machine, track, x: e.clientX - rect.left, y: e.clientY - rect.top, cw: rect.width, ch: rect.height });
              }}
              onMouseLeave={() => setHover(null)}
              aria-label={`${jobLabel(j)} on ${machine}${plex ? " (Plex schedule)" : track === "optimized" ? " (Optimized)" : ""}${clickable ? " — click to move" : ""}`}
            >
              {/* Option B — the stretches of this bar that span unstaffed hours,
                  hatched: paused there, not running. Never on a job a person
                  placed at an exact time. */}
              {!hatchExempt(j)
                ? unstaffedSpans({ from, to: from + width }, bands).map((s, k) => (
                    <span key={k} className="boardJobUnstaffed" aria-hidden="true" style={{ left: `${s.from * 100}%`, width: `${(s.to - s.from) * 100}%` }} />
                  ))
                : null}
              {j.pinned && !j.placement ? <span className="boardJobLock"><IconLock /></span> : null}
              {track === "working" && movedMarkerVisible(j) ? <span className="boardJobMovedDot" aria-hidden="true" /> : null}
              <span className="boardJobLabel">{jobLabel(j)}</span>
            </button>
          </div>
        );
      })}
    </div>
    );
  };

  return (
    <div className="boardLanes">
      <div className="boardLanesHeader">
        <div className="boardZoom" role="group" aria-label="Zoom">
          {ZOOMS.map((z) => (
            <button
              key={z.key}
              type="button"
              className={`btn btnSm${zoom === z.key ? " boardZoomActive" : ""}`}
              onClick={() => setZoom(z.key)}
            >
              {z.label}
            </button>
          ))}
        </div>
        <div className="boardZoom" role="group" aria-label="Expand lanes">
          <button type="button" className="btn btnSm" onClick={props.onExpandAll}>
            Expand all
          </button>
          <button type="button" className="btn btnSm" onClick={props.onCollapseAll}>
            Collapse all
          </button>
        </div>
        {props.findSlot}
        <div className="muted boardLanesRange">
          {formatTimestamp(new Date(window.startMs).toISOString())} — {formatTimestamp(new Date(window.endMs).toISOString())}
        </div>
      </div>
      {/* Labeled time axis over the lane stack (gridlines repeat in each track). */}
      <div className="boardAxisRow" aria-hidden="true">
        <div />
        <div className="boardAxisTrack">
          {ticks
            // An hour label under the "Now" tag reads as debris (measured
            // 16px overlap) — the tag wins; dated labels keep their slot
            // since the tag's own background covers them cleanly.
            .filter((t) => t.label != null && !(showNow && t.kind === "hour" && Math.abs(t.frac - nowFrac) < 0.045))
            .map((t) => (
              <span
                key={t.ms}
                className={`boardAxisLabel${t.kind === "day" ? " boardAxisLabelDay" : ""}`}
                style={{ left: `${t.frac * 100}%` }}
              >
                {t.label}
              </span>
            ))}
          {showNow ? (
            <span className="boardAxisNow" style={{ left: `${nowFrac * 100}%` }}>
              Now
            </span>
          ) : null}
        </div>
      </div>
      <div className="boardLaneRows">
        {props.lanes.map((lane) => {
          const expanded = props.expandedMachines.has(lane.machine);
          const scheduledLane = scheduledByMachine.get(lane.machine);
          const optimizedLane = optimizedByMachine.get(lane.machine);
          // The dispatcher's line leads when there is one to lead with.
          const primaryIsPlex = !!scheduledLane && scheduledLane.jobs.length > 0;
          return (
            <div key={lane.machine} style={{ display: "contents" }}>
              <div className="boardLaneRow" id={`lane-${lane.machine}`}>
                {/* Labels live ON rows: the machine name (the toggle) and,
                    when expanded, the working track's bare label in the
                    same cell — never a caption liner. */}
                <div className="boardLaneLabelCell">
                  <button
                    type="button"
                    className="boardLaneLabel boardLaneToggle"
                    aria-expanded={expanded}
                    onClick={() => props.onToggleMachine(lane.machine)}
                  >
                    {lane.machine}
                  </button>
                  {expanded ? (
                    <span className="boardLaneTrackLabel">{primaryIsPlex ? "Plex" : props.workingLabel}</span>
                  ) : null}
                  {/* Plex routinely carries more than one started op on a
                      machine. Say so, rather than leaving two crossing bars
                      with no explanation. */}
                  {primaryIsPlex && (scheduledLane!.plex_started_count ?? 0) > 1 ? (
                    <span className="muted boardLaneTrackNote">
                      {scheduledLane!.plex_started_count} jobs started here
                    </span>
                  ) : null}
                </div>
                {primaryIsPlex
                  ? renderTrack(lane.machine, scheduledLane!.jobs, "plex")
                  : renderTrack(lane.machine, lane.jobs, "working")}
              </div>
              {/* Iteration 3 item D — Plex is the PRIMARY line: the collapsed
                  grid shows what the dispatcher has booked, and expanding adds
                  our two plans beneath it, Optimized then Refined. Expanding
                  still only ADDS same-height rows; it never inflates.
                  A model with no Plex binding has no Plex line to lead with, so
                  the working row stays primary there rather than leaving the
                  collapsed grid blank. */}
              {expanded && props.optimizedLanes ? (
                <div className="boardLaneRow boardLaneSubRow">
                  <div className="boardLaneLabelCell">
                    <span className="boardLaneTrackLabel">Optimized</span>
                  </div>
                  {optimizedLane && optimizedLane.jobs.length > 0 ? (
                    renderTrack(lane.machine, optimizedLane.jobs, "optimized")
                  ) : (
                    <div className="help" style={{ margin: 0 }}>No standing plan runs this machine.</div>
                  )}
                </div>
              ) : null}
              {expanded && primaryIsPlex ? (
                <div className="boardLaneRow boardLaneSubRow">
                  <div className="boardLaneLabelCell">
                    <span className="boardLaneTrackLabel">{props.workingLabel}</span>
                  </div>
                  {renderTrack(lane.machine, lane.jobs, "working")}
                </div>
              ) : null}
              {expanded && !primaryIsPlex ? (
                <div className="boardLaneRow boardLaneSubRow">
                  <div className="boardLaneLabelCell">
                    <span className="boardLaneTrackLabel">Plex</span>
                  </div>
                  <div className="help" style={{ margin: 0 }}>
                    The Plex schedule track arrives with the next solve.
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {hover?.kind === "changeover" ? (
        <ChangeoverHoverCard
          job={hover.job}
          measuredCounts={props.measuredCounts}
          place={hoverCardPosition(hover.x, hover.y, hover.cw, hover.ch)}
        />
      ) : null}
      {hover?.kind === "gap" ? (
        <div className="boardHoverCard" style={hoverCardPosition(hover.x, hover.y, hover.cw, hover.ch)}>
          <Note><strong>Before {jobLabel(hover.job)}</strong></Note>
          {gapHoverRows(hover.job).map((r) => (
            <Row key={r.label} label={r.label}>
              {r.muted ? <span className="muted">{r.value}</span> : r.value}
            </Row>
          ))}
        </div>
      ) : null}
      {hover?.kind === "job" ? (
        <div className="boardHoverCard" style={hoverCardPosition(hover.x, hover.y, hover.cw, hover.ch)}>
          <Note>
            <strong className="mono">{hover.job.job}</strong>
            {hover.job.part ? <span> · {hover.job.part}</span> : null}
            <span className="muted"> — {hover.machine}</span>
          </Note>

          {/* WHEN. Every track draws a laid-out time. The Plex line is Plex's
              ORDER laid out from now (owner, 2026-10-08), so Plex's own booked
              start rides beside it — overdue when it is earlier than the drawn
              start. */}
          <Row label="Start">{formatTimestamp(hover.job.start)}</Row>
          <Row label="End">{formatTimestamp(hover.job.end)}</Row>
          {hover.track === "plex" ? (
            hover.job.running ? (
              <Row label="Plex">Running now — first on this machine</Row>
            ) : hover.job.plex_scheduled_start ? (
              <Row label="Plex start">
                {formatTimestamp(hover.job.plex_scheduled_start)}
                {plexOverdue(hover.job) ? <span className="muted"> — overdue</span> : null}
              </Row>
            ) : (
              <Row label="Plex">No scheduled start</Row>
            )
          ) : null}

          {/* The runner's projection is computed at SOLVE time, so it goes
              stale as the shift moves. Say it only where it actually differs
              from the end being drawn, and say which clock it came off — once
              the drawn end allows for unstaffed hours itself, the two agree
              and this line disappears on its own. */}
          {hover.job.projected_end && hover.job.projected_end !== hover.job.end ? (
            <Row label="Projected">
              {formatTimestamp(hover.job.projected_end)}
              <span className="muted"> (at solve time)</span>
            </Row>
          ) : null}

          {hover.job.running && hover.job.remaining_run_hours != null ? (
            <>
              {hover.job.run_started_at ? (
                <Row label="Since">{formatTimestamp(hover.job.run_started_at)}</Row>
              ) : null}
              <Row label="Remaining">{hover.job.remaining_run_hours} h</Row>
              {hover.job.remaining_hours_basis ? <Basis>{hover.job.remaining_hours_basis}</Basis> : null}
            </>
          ) : null}

          {/* PROMISE. */}
          {hover.job.promise_due_date ? (
            <>
              <Row label="Promise">
                {formatTimestamp(hover.job.promise_due_date)}
                {hover.job.at_risk ? <span className="muted"> — at risk</span> : null}
              </Row>
              <Row label="Milestone">
                {hover.job.due_date ? formatTimestamp(hover.job.due_date) : "—"}
              </Row>
            </>
          ) : (
            <Row label="Due">
              {hover.job.due_date ? formatTimestamp(hover.job.due_date) : "—"}
              {hover.job.at_risk ? <span className="muted"> — at risk</span> : null}
            </Row>
          )}

          {/* The badge and the bar answer different questions on the Plex
              track: the bar shows Plex's ORDER, the badge asks whether Plex's
              DATES miss the promise. Name which claim was judged. */}
          {hover.track === "plex" && hover.job.promise_due_date && hover.job.plex_risk_basis ? (
            <Row label="Judged on">
              {hover.job.plex_risk_basis === "remaining_hours" ? "Hours still to run" : "Plex's booked start"}
            </Row>
          ) : null}

          {/* WORK. */}
          {(() => {
            const pieces = pieceLine(hover.job);
            return pieces ? (
              <Row label="Pieces">
                {pieces.text}
                {pieces.share ? <span className="muted"> ({pieces.share})</span> : null}
              </Row>
            ) : null;
          })()}
          <Row label="Run time">{hover.job.run_hours ?? "—"} h</Row>
          {hover.job.machine_moved_from ? (
            <Basis>assumed — {hover.job.machine_moved_from}'s figure</Basis>
          ) : hover.job.run_hours_basis ? (
            <Basis>{hover.job.run_hours_basis}</Basis>
          ) : null}
          {hover.job.components.map((c) => (
            <Row key={c.label} label={c.label}>
              {c.value ?? "—"}
              {c.basis ? <span className="muted"> ({c.basis})</span> : null}
            </Row>
          ))}
          {hover.job.setup_class ? (
            <Row label="Changeover">
              {hover.job.setup_class} · {hover.job.setup_hours} h
              {hover.job.setup_cost_fallback ? <span className="muted"> (estimated)</span> : null}
            </Row>
          ) : null}

          {/* EXCEPTIONS — only present when they apply, so their presence is
              itself the signal. */}
          {hover.job.plex_op_state && hover.job.plex_op_state !== "running" ? (
            <Row label="Plex">
              {hover.job.plex_op_state === "finished_open"
                ? "Shows started — every piece made"
                : hover.job.plex_op_state === "superseded"
                  ? "Shows started — balance re-issued"
                  : "Not on the machine now"}
            </Row>
          ) : null}
          {/* Approved machines (ADR-0183 Amendment 4) — Option A, the owner's
              pick: the whole set on every job's hover, all three tracks. The
              rows are omitted entirely while the card is unconnected, so
              their presence is itself the signal that the routing is in play.
              Plain text: colour alone never carries meaning. */}
          {hover.job.approved_machines && hover.job.approved_machines.length > 0 ? (
            <Row label="Can run on">
              {hover.job.approved_machines.map((a, i) => (
                <span key={a.machine}>
                  {i > 0 ? <span className="muted"> · </span> : null}
                  {a.machine === hover.machine ? <strong>{a.machine}</strong> : a.machine}
                  {a.note ? <span className="muted"> ({a.note})</span> : null}
                </span>
              ))}
            </Row>
          ) : hover.job.machine_approved === false ? (
            <Row label="Can run on">No approved machines on record</Row>
          ) : null}
          {hover.job.machine_approved === false && hover.job.approved_machines && hover.job.approved_machines.length > 0 ? (
            <Row label="Routing">
              Not approved here — {hover.machine} isn't in {hover.job.part ?? "this part"}'s approved machines
            </Row>
          ) : null}
          {hover.job.machine_basis === "log" && hover.job.routed_machine && hover.job.routed_machine !== hover.machine ? (
            <Row label="Routed">
              {hover.job.routed_machine}
              <span className="muted"> (running here — its log rows)</span>
            </Row>
          ) : null}
          {rankChangeLine(hover.job) ? <Row label="Moved">{rankChangeLine(hover.job)}</Row> : null}
          {hover.job.placement && hover.track === "working" ? (
            <Row label="Your move">
              {hover.job.placement.kind === "best_fit" ? "Best fit"
                : hover.job.placement.kind === "run_next" ? "Run next"
                  : hover.job.placement.kind === "before_after" ? `${hover.job.placement.side} ${hover.job.placement.anchor_job}`
                    : hover.job.placement.kind === "exact_time" ? "Exact time" : "Near a date"}
            </Row>
          ) : hover.job.pinned ? (
            <Row label={hover.job.running ? "Held" : "Pinned"}>
              {hover.job.running ? "Running — held in place" : "In the planner's order"}
            </Row>
          ) : null}

          {/* One muted footer, not a paragraph per note. */}
          {(() => {
            const notes: string[] = [];
            if (hover.track === "plex") notes.push("Plex's order, laid out from now");
            if (hover.track === "optimized") notes.push("The standing plan");
            if (movableOnTrack(hover.track, hover.job, !!props.onJobClick)) notes.push("click to move");
            return notes.length ? <Note muted>{notes.join(" · ")}</Note> : null;
          })()}
        </div>
      ) : null}
      {/* Legend — names every encoding exactly as the code above draws it,
          with swatches carrying the same classes the lanes use. */}
      <div className="boardLegend">
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardJobSegment" />a job — labeled by job number (part)
        </span>
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardJobSegment boardJobRunning" />running now
        </span>
        <span className="boardLegendItem">
          <span className="boardJobLock"><IconLock /></span>held in place
        </span>
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardJobSegment boardJobAtRisk" />promise at risk
        </span>
        <span className="boardLegendItem">
          <span
            className="boardLegendRamp"
            style={{ background: `linear-gradient(90deg, ${setupColor(0, 1)}, ${setupColor(0.5, 1)}, ${setupColor(1, 1)})` }}
          />
          changeover: shorter → longer
        </span>
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardSetupGap boardSetupGapEstimated" style={{ background: setupColor(0.5, 1) }} />
          estimated (no measured class)
        </span>
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardShadeUnstaffed" />unstaffed hours
        </span>
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardJobSegment boardLegendHatched" />
          spans unstaffed hours — paused, not running (a job you placed at a time stays solid)
        </span>
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardShadeReduced" />reduced staffing
        </span>
        {props.optimizedLanes ? (
          <span className="boardLegendItem">
            <span className="boardLegendSwatch boardJobSegment boardLegendOptimized" />Optimized track (the standing plan)
          </span>
        ) : null}
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardJobSegment boardLegendPlex" />Plex track (Plex's order, laid out from now)
        </span>
        {props.onJobClick ? (
          <>
            <span className="boardLegendItem">
              <span className="boardLegendSwatch boardJobSegment boardJobPending" />move pending
            </span>
            <span className="boardLegendItem">
              <span className="boardLegendSwatch boardJobSegment boardJobPlaced" />held by your move
            </span>
          </>
        ) : null}
        <span className="boardLegendItem">
          <span className="boardLegendSwatch boardJobSegment boardLegendMoved"><span className="boardJobMovedDot" /></span>
          differs from the Plex schedule
        </span>
      </div>
    </div>
  );
}
